import {
  CaretUp,
  MagnifyingGlass,
  X,
  Plus,
  CheckCircle,
} from '@phosphor-icons/react';
import React, { useState, useRef, useEffect } from 'react';
import { AnalyzerWeeklyPrompt } from '@Models/types';
import { useAnalyzerReportsContext } from '@Context/AnalyzerReportsContext';
import { CreatePromptModal } from './prompts/CreatePromptModal';
import { EditPromptModal } from './prompts/EditPromptModal';
import { ViewPromptModal } from './prompts/ViewPromptModal';
import { DeletePromptModal } from './prompts/DeletePromptModal';

interface PromptDropdownProps {
  isAdmin?: boolean;
}

export const PromptDropdown: React.FC<PromptDropdownProps> = ({
  isAdmin = false,
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [settingDefault, setSettingDefault] = useState(false);

  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedModalPrompt, setSelectedModalPrompt] =
    useState<AnalyzerWeeklyPrompt | null>(null);

  const { prompts, selectedPrompt, promptsLoading, setDefaultPrompt } =
    useAnalyzerReportsContext();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
        setSearchTerm('');
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  const filteredPrompts =
    isAdmin && searchTerm
      ? prompts.filter((prompt) =>
          prompt.promptTitle.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : prompts;

  const handleSetDefaultPrompt = async (
    e: React.MouseEvent,
    prompt: AnalyzerWeeklyPrompt
  ) => {
    e.stopPropagation();
    setSettingDefault(true);
    try {
      await setDefaultPrompt(prompt.promptTitle, prompt.authorEmail);
    } finally {
      setSettingDefault(false);
    }
  };

  const openViewModal = (prompt: AnalyzerWeeklyPrompt) => {
    setSelectedModalPrompt(prompt);
    setViewModalOpen(true);
    setOpen(false);
  };

  const openEditModal = (prompt: AnalyzerWeeklyPrompt) => {
    setSelectedModalPrompt(prompt);
    setViewModalOpen(false);
    setEditModalOpen(true);
    setOpen(false);
  };

  const openDeleteModal = (prompt: AnalyzerWeeklyPrompt) => {
    setSelectedModalPrompt(prompt);
    setViewModalOpen(false);
    setDeleteModalOpen(true);
    setOpen(false);
  };

  const closeModals = () => {
    setCreateModalOpen(false);
    setEditModalOpen(false);
    setViewModalOpen(false);
    setDeleteModalOpen(false);
    setSelectedModalPrompt(null);
  };

  return (
    <div className="relative w-72" ref={dropdownRef}>
      <button
        type="button"
        className="flex w-full items-center justify-between rounded-md border bg-white px-4 py-2 font-inter text-sm focus:outline-none focus:ring-0"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={promptsLoading || settingDefault}
      >
        <span className="flex items-center gap-2 whitespace-nowrap">
          {settingDefault ? (
            <>
              <div className="h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-green-500" />
              Setting default...
            </>
          ) : prompts.length > 0 ? (
            (() => {
              const defaultPrompt = prompts.find((p) => p.isDefault);
              return defaultPrompt ? (
                <>
                  <CheckCircle
                    size={12}
                    weight="fill"
                    className="text-green-500"
                  />
                  {defaultPrompt.promptTitle}
                  <span className="text-xs text-green-600">(Default)</span>
                </>
              ) : (
                prompts[0]?.promptTitle || 'Select Prompt'
              );
            })()
          ) : (
            'No Prompts Available'
          )}
        </span>
        <CaretUp
          size={18}
          className={`ml-2 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <ul
          className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-white py-1 shadow-lg focus:outline-none"
          tabIndex={-1}
          role="listbox"
        >
          {isAdmin && (
            <>
              <li className="px-4 py-2">
                <div className="flex w-full flex-row items-center gap-x-2 rounded-md border px-2 py-1">
                  <MagnifyingGlass size={16} weight="bold" />
                  <input
                    type="text"
                    className="w-full border-gray-300 font-inter text-xs focus:outline-none sm:text-sm"
                    placeholder="Search prompts..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  {searchTerm && (
                    <span
                      className="cursor-pointer hover:text-brandRed-500"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSearchTerm('');
                      }}
                    >
                      <X size={16} weight="bold" />
                    </span>
                  )}
                </div>
              </li>
              <li className="border-b border-gray-200">
                <button
                  className="flex w-full items-center gap-2 px-4 py-2 text-sm text-primary-600 hover:bg-primary-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCreateModalOpen(true);
                    setOpen(false);
                  }}
                >
                  <Plus size={16} weight="bold" />
                  New Prompt
                </button>
              </li>
            </>
          )}
          {filteredPrompts.length === 0 && (
            <li className="flex cursor-pointer items-center justify-between px-4 py-2 text-sm hover:bg-primary-100">
              <span className="flex w-full flex-row items-center justify-between">
                <span>
                  {isAdmin && searchTerm
                    ? 'No prompts match your search'
                    : 'No Prompts Available'}
                </span>
              </span>
            </li>
          )}
          {filteredPrompts.map((prompt) => (
            <li
              key={prompt._id}
              className={`group relative flex cursor-pointer items-center justify-between px-4 py-2 text-sm hover:bg-primary-100 ${
                prompt.promptTitle === selectedPrompt?.promptTitle &&
                prompt.authorEmail === selectedPrompt?.authorEmail
                  ? 'bg-primary-50 font-semibold'
                  : ''
              }`}
              onClick={() => {
                openViewModal(prompt);
              }}
              role="option"
              aria-selected={
                prompt.promptTitle === selectedPrompt?.promptTitle &&
                prompt.authorEmail === selectedPrompt?.authorEmail
              }
            >
              <span className="flex items-center gap-2">
                {prompt.isDefault && (
                  <CheckCircle
                    size={12}
                    weight="fill"
                    className="text-green-500"
                  />
                )}
                {prompt.promptTitle}
                {prompt.isDefault && (
                  <span className="text-xs text-green-600">(Default)</span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* Modals */}
      <CreatePromptModal isOpen={createModalOpen} onClose={closeModals} />

      <EditPromptModal
        isOpen={editModalOpen}
        onClose={closeModals}
        prompt={selectedModalPrompt}
      />

      <ViewPromptModal
        isOpen={viewModalOpen}
        onClose={closeModals}
        prompt={selectedModalPrompt}
        isAdmin={isAdmin}
        onEdit={openEditModal}
        onDelete={openDeleteModal}
        onSetDefault={(prompt) => {
          handleSetDefaultPrompt(
            { stopPropagation: () => {} } as unknown as React.MouseEvent,
            prompt
          );
        }}
        currentDefaultPrompt={prompts.find((p) => p.isDefault) || null}
      />

      <DeletePromptModal
        isOpen={deleteModalOpen}
        onClose={closeModals}
        prompt={selectedModalPrompt}
      />
    </div>
  );
};
